import React, { memo, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';

import { Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import { PeopleIcon, SearchIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import { TELNYX_SIP_DOMAIN, UserStatus, UserStatusLabels } from 'constants/telephony';
import useAutofocusSearchOnOpen from 'hooks/useAutofocusSearchOnOpen';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useFocusSearchOnType from 'hooks/useFocusSearchOnType';
import { getIsNativePlatform } from 'utils/platform';

import {
  fetchContactsAction,
  makeOutgoingCallAction,
  setContactsSearchAction,
} from 'store/system/actions';
import {
  selectContactsLoading,
  selectContactsSearchQuery,
  selectFilteredContacts,
  selectIsTelnyxConnected,
} from 'store/system/selectors';

import { getStatusColor } from '../../TelephonyWidget.utils';

import {
  contactInfoStyles,
  contactNameStyles,
  contactRowDisabledStyles,
  contactRowStyles,
  contactsContainerStyles,
  contactsListStyles,
  contactStatusDotStyles,
  contactStatusLabelStyles,
  emptyStateIconStyles,
  emptyStateStyles,
  emptyStateTextStyles,
  loadingContainerStyles,
  searchBoxStyles,
  searchIconStyles,
  searchInputStyles,
} from './Contacts.styles';
import { ContactsTestIds } from './Contacts.testIds';

const searchAdornment = (
  <InputAdornment position="start">
    <Box sx={searchIconStyles}>
      <SearchIcon size={IconSize[20]} />
    </Box>
  </InputAdornment>
);

const inputProps = { startAdornment: searchAdornment };

// Row height: 10px padding top + 10px padding bottom + ~20px content = 40px
const CONTACT_ROW_HEIGHT = 50;

const ContactRow = memo(({ contact, onCall, isTelnyxConnected }) => {
  const { firstName, lastName, contactStatus, sipUsername } = contact;

  const isCallDisabled =
    !sipUsername ||
    !isTelnyxConnected ||
    contactStatus === UserStatus.OUT_OF_OFFICE ||
    contactStatus === UserStatus.UNAVAILABLE ||
    contactStatus === UserStatus.ON_CALL;

  const statusColor = useMemo(() => getStatusColor(contactStatus), [contactStatus]);

  const dotStyles = useMemo(
    () => ({ ...contactStatusDotStyles, backgroundColor: statusColor }),
    [statusColor],
  );

  const handleClick = useCallback(() => {
    if (isCallDisabled) {
      return;
    }

    onCall(contact);
  }, [contact, isCallDisabled, onCall]);

  const rowStyles = isCallDisabled ? contactRowDisabledStyles : contactRowStyles;

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <Box
      role="button"
      sx={rowStyles}
      tabIndex={isCallDisabled ? -1 : 0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Box sx={dotStyles} />
      <Box sx={contactInfoStyles}>
        <Typography sx={contactNameStyles} variant="regular14">
          {firstName} {lastName}
        </Typography>
        <Typography sx={contactStatusLabelStyles} variant="regular12">
          {UserStatusLabels[contactStatus] ?? contactStatus}
        </Typography>
      </Box>
    </Box>
  );
});

ContactRow.displayName = 'ContactRow';

ContactRow.propTypes = {
  contact: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    contactStatus: PropTypes.string,
    sipUsername: PropTypes.string,
    userId: PropTypes.string,
  }).isRequired,
  isTelnyxConnected: PropTypes.bool.isRequired,
  onCall: PropTypes.func.isRequired,
};

const Contacts = () => {
  const dispatch = useDispatch();
  const contacts = useSelector(selectFilteredContacts);
  const isLoading = useSelector(selectContactsLoading);
  const searchQuery = useSelector(selectContactsSearchQuery);
  const isTelnyxConnected = useSelector(selectIsTelnyxConnected);

  // Native uses lazy on-demand SIP registration (connects at dial time), so the contact call
  // button must not require a standing connection. Pass the effective connected value so
  // ContactRow is enabled on native even when the SIP client is idle.
  const isNative = getIsNativePlatform();
  const searchInputRef = useRef(null);

  useEffectSafeStrictMode(() => {
    dispatch(fetchContactsAction());
  }, [dispatch]);

  const onSearchChange = useCallback(
    e => {
      dispatch(setContactsSearchAction(e.target.value));
    },
    [dispatch],
  );

  const onTypeSearch = useCallback(
    char => {
      dispatch(setContactsSearchAction(char));
    },
    [dispatch],
  );

  const onCallContact = useCallback(
    contact => {
      const { sipUsername } = contact;

      if (!sipUsername) {
        return;
      }

      dispatch(
        makeOutgoingCallAction({
          destinationNumber: `${sipUsername}@${TELNYX_SIP_DOMAIN}`,
        }),
      );
    },
    [dispatch],
  );

  const renderContactRow = useCallback(
    ({ index, key, style }) => {
      const contact = contacts[index];

      return (
        <div key={key} style={style}>
          <ContactRow
            contact={contact}
            isTelnyxConnected={isNative || isTelnyxConnected}
            onCall={onCallContact}
          />
        </div>
      );
    },
    [contacts, isNative, isTelnyxConnected, onCallContact],
  );

  const noContactsRenderer = useCallback(
    () => (
      <Box sx={emptyStateStyles}>
        <Box sx={emptyStateIconStyles}>
          <PeopleIcon size={IconSize[48]} />
        </Box>
        <Typography sx={emptyStateTextStyles} variant="regular14">
          {searchQuery ? 'No contacts match your search' : 'No contacts available'}
        </Typography>
      </Box>
    ),
    [searchQuery],
  );

  useAutofocusSearchOnOpen({ isOpen: !isLoading, inputRef: searchInputRef });
  useFocusSearchOnType({ enabled: !isLoading, inputRef: searchInputRef, onType: onTypeSearch });

  if (isLoading) {
    return (
      <Box sx={contactsContainerStyles}>
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={contactsContainerStyles}>
      <Box sx={searchBoxStyles}>
        <TextField
          fullWidth
          InputProps={inputProps}
          data-testid={ContactsTestIds.SEARCH}
          inputRef={searchInputRef}
          placeholder="Search contacts..."
          size="small"
          sx={searchInputStyles}
          value={searchQuery}
          onChange={onSearchChange}
        />
      </Box>

      <Box sx={contactsListStyles}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              height={height}
              noRowsRenderer={noContactsRenderer}
              overscanRowCount={5}
              rowCount={contacts.length}
              rowHeight={CONTACT_ROW_HEIGHT}
              rowRenderer={renderContactRow}
              width={width}
            />
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
};

export default memo(Contacts);
